import { useEffect, useState } from "react";

const NAV_PX = 72;

/**
 * Show a down-chevron only when the target is below the fold (not above it)
 * and the sticky mobile price bar is not already on screen.
 */
function shouldShowDownNudge(el) {
  if (!el) return false;
  if (
    typeof document !== "undefined" &&
    document.body.classList.contains("lp2-has-mobile-price-bar")
  ) {
    return false;
  }
  const rect = el.getBoundingClientRect();
  const inView =
    rect.top < window.innerHeight - NAV_PX && rect.bottom > NAV_PX;
  if (inView) return false;
  // Below the fold → scroll down makes sense. Above (user scrolled past) → hide.
  return rect.top > window.innerHeight - NAV_PX;
}

/**
 * True when `targetId` is off-screen below the user (scroll down to reach it).
 * Used for a small bounce hint on mobile during onboarding / booking.
 */
export function useScrollNudge(targetId, enabled) {
  const [nudge, setNudge] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setNudge(false);
      return undefined;
    }

    let observedEl = null;

    const refresh = () => {
      const el = document.getElementById(targetId);
      if (!el) {
        setNudge(false);
        observedEl = null;
        return;
      }
      observedEl = el;
      setNudge(shouldShowDownNudge(el));
    };

    refresh();
    const retryId = requestAnimationFrame(refresh);
    window.addEventListener("scroll", refresh, { passive: true });
    window.addEventListener("resize", refresh);

    let observer = null;
    const attachObserver = () => {
      const el = document.getElementById(targetId);
      if (!el) return;
      if (!observer) {
        observer = new IntersectionObserver(() => refresh(), {
          threshold: 0,
          rootMargin: `-${NAV_PX}px 0px 0px 0px`,
        });
      }
      if (observedEl && observedEl !== el) observer.unobserve(observedEl);
      observer.observe(el);
      observedEl = el;
    };
    attachObserver();

    return () => {
      cancelAnimationFrame(retryId);
      window.removeEventListener("scroll", refresh);
      window.removeEventListener("resize", refresh);
      if (observer) observer.disconnect();
    };
  }, [targetId, enabled]);

  return nudge;
}
